import { CHANGE, POOL, coinValues, countOf, totalOf, totalToShare, type RedistributionState } from '../../shared/redistribution';
import { detectPlatform } from './media';
import { downloadFile } from './recordingFiles';
import { xlsxFile, type Cell, type Sheet } from './xlsx';

// What the board looks like and what is kept at the end: the Excel table and the board picture.

export type CoinShape = 'triangle' | 'square' | 'circle' | 'diamond' | 'hexagon' | 'star';

/** Largest value first: yellow triangle, green square, blue circle… like the board on Miro. */
const STYLES: { shape: CoinShape; color: string; ink: string }[] = [
  { shape: 'triangle', color: '#f5b800', ink: '#5c4300' },
  { shape: 'square', color: '#b5d334', ink: '#3b4a00' },
  { shape: 'circle', color: '#2f8cf0', ink: '#ffffff' },
  { shape: 'diamond', color: '#9b6bdf', ink: '#ffffff' },
  { shape: 'hexagon', color: '#f07c3a', ink: '#ffffff' },
  { shape: 'star', color: '#e2558f', ink: '#ffffff' },
];

export function coinStyle(values: number[], value: number) {
  const rank = values.indexOf(value);
  return STYLES[Math.max(0, rank) % STYLES.length];
}

export const fmt = (n: number) => n.toLocaleString('es');

export function percent(part: number, whole: number): string {
  if (!whole) return '0 %';
  return `${(Math.round((part / whole) * 1000) / 10).toLocaleString('es')} %`;
}

export interface ResultRow {
  name: string;
  counts: number[];
  total: number;
}

/** Each circle, then the common pool, with coins per value (largest first) and totals. */
export function results(s: RedistributionState): { values: number[]; rows: ResultRow[]; pool: ResultRow; total: number } {
  const values = coinValues(s.holdings);
  const row = (name: string, place: string): ResultRow => ({ name, counts: values.map((v) => countOf(s.holdings, place, v)), total: totalOf(s.holdings, place) });
  return {
    values,
    rows: s.circles.map((c) => row(c.name, c.id)),
    pool: row('Fondo común (sin repartir)', POOL),
    total: totalToShare(s.holdings),
  };
}

function stamp(at: number | null): string {
  const d = new Date(at ?? Date.now());
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}.${p(d.getMinutes())}`;
}

export function exportName(s: RedistributionState, ext: string): string {
  const title = s.title.replace(/[\\/:*?"<>|\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Redistribución';
  return `${title} - ${stamp(s.finishedAt ?? s.startedAt)}.${ext}`;
}

export function resultSheet(s: RedistributionState): Sheet {
  const r = results(s);
  const when = new Date(s.finishedAt ?? s.startedAt ?? Date.now()).toLocaleString('es');
  const header: Cell[] = ['Nombre', ...r.values.map((v) => `Fichas de ${fmt(v)}`), 'Total', '% del total'];
  const line = (x: ResultRow): Cell[] => [x.name, ...x.counts, x.total, r.total ? Math.round((x.total / r.total) * 1000) / 10 : 0];
  const rows: Cell[][] = [
    [s.title || 'Redistribución'],
    ['Fecha', when],
    ['Rondas', s.round],
    ['Estado', s.status === 'finished' ? 'Terminada' : 'En curso'],
    [],
    header,
    ...r.rows.map(line),
    line(r.pool),
    ['Total', ...r.values.map((_, i) => r.rows.reduce((n, x) => n + x.counts[i], r.pool.counts[i])), r.total, r.total ? 100 : 0],
  ];
  return { name: 'Redistribución', rows, bold: [0, 5, rows.length - 1], widths: [30, ...r.values.map(() => 14), 12, 12] };
}

export function excelFile(s: RedistributionState): File {
  return xlsxFile([resultSheet(s)], exportName(s, 'xlsx'));
}

// ───────────────────────────── board picture ─────────────────────────────

function drawShape(ctx: CanvasRenderingContext2D, shape: CoinShape, x: number, y: number, r: number): void {
  ctx.beginPath();
  if (shape === 'circle') ctx.arc(x, y, r, 0, Math.PI * 2);
  else if (shape === 'square') ctx.rect(x - r * 0.85, y - r * 0.85, r * 1.7, r * 1.7);
  else {
    const points = shape === 'triangle' ? 3 : shape === 'diamond' ? 4 : shape === 'hexagon' ? 6 : 10;
    for (let i = 0; i < points; i++) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / points;
      const rr = shape === 'star' && i % 2 ? r * 0.5 : shape === 'triangle' ? r * 1.15 : r;
      const px = x + rr * Math.cos(a);
      const py = y + rr * Math.sin(a) + (shape === 'triangle' ? r * 0.2 : 0);
      if (i) ctx.lineTo(px, py);
      else ctx.moveTo(px, py);
    }
    ctx.closePath();
  }
  ctx.fill();
}

/** Chips of a place: one coin per value with "×n". Returns the height used. */
function drawChips(ctx: CanvasRenderingContext2D, s: RedistributionState, values: number[], place: string, cx: number, top: number, maxWidth: number): number {
  const chips = values.filter((v) => countOf(s.holdings, place, v) > 0);
  if (!chips.length) return 0;
  const w = 84;
  const perRow = Math.max(1, Math.floor(maxWidth / w));
  chips.forEach((v, i) => {
    const row = Math.floor(i / perRow);
    const inRow = Math.min(perRow, chips.length - row * perRow);
    const x = cx - (inRow * w) / 2 + (i % perRow) * w + 20;
    const y = top + row * 42 + 18;
    const st = coinStyle(values, v);
    ctx.fillStyle = st.color;
    drawShape(ctx, st.shape, x, y, 16);
    ctx.fillStyle = st.ink;
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(fmt(v), x, y + (st.shape === 'triangle' ? 3 : 0));
    ctx.fillStyle = '#1d2939';
    ctx.font = '600 16px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`×${countOf(s.holdings, place, v)}`, x + 22, y);
  });
  return Math.ceil(chips.length / perRow) * 42;
}

/** The board as a PNG, laid out like the board in the meeting (circles around the common pool). */
export async function boardImage(s: RedistributionState): Promise<File> {
  const W = 1600;
  const H = 1100;
  const canvas = Object.assign(document.createElement('canvas'), { width: W, height: H });
  const ctx = canvas.getContext('2d')!;
  const values = coinValues(s.holdings);
  const total = totalToShare(s.holdings);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = '#1d2939';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = 'bold 44px sans-serif';
  ctx.fillText(s.title || 'Redistribución', 48, 76);
  ctx.font = '22px sans-serif';
  ctx.fillStyle = '#475467';
  ctx.fillText(
    `${new Date(s.finishedAt ?? s.startedAt ?? Date.now()).toLocaleString('es')} · ${s.circles.length} círculos · ${fmt(total)} en total · ${s.round} ${s.round === 1 ? 'ronda' : 'rondas'}${s.status === 'finished' ? ' · terminada' : ''}`,
    48,
    112,
  );

  // Common pool in the middle, circles around it.
  const cx = W / 2;
  const cy = 150 + (H - 150) / 2;
  const rx = W * 0.36;
  const ry = (H - 150) * 0.37;
  const n = s.circles.length;
  const place = (id: string, name: string, x: number, y: number, dark: boolean) => {
    const R = n > 16 ? 42 : 56;
    ctx.fillStyle = dark ? '#16181d' : '#eef1f5';
    ctx.beginPath();
    ctx.arc(x, y, R, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = dark ? '#ffffff' : '#1d2939';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `bold ${name.length > 12 ? 15 : 18}px sans-serif`;
    ctx.fillText(name.length > 18 ? `${name.slice(0, 17)}…` : name, x, y - 9);
    ctx.font = '600 20px sans-serif';
    ctx.fillText(fmt(totalOf(s.holdings, id)), x, y + 16);
    drawChips(ctx, s, values, id, x, y + R + 6, 260);
  };
  place(POOL, 'Fondo común', cx, cy - 30, false);
  s.circles.forEach((c, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / Math.max(1, n);
    place(c.id, c.name, cx + rx * Math.cos(a), cy - 40 + ry * Math.sin(a), true);
  });

  // Change reserve, bottom left, smaller.
  ctx.fillStyle = '#475467';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = 'bold 18px sans-serif';
  ctx.fillText(`CAMBIO (${fmt(totalOf(s.holdings, CHANGE))})`, 48, H - 70);
  drawChips(ctx, s, values, CHANGE, 160, H - 60, 260);

  const blob = await new Promise<Blob>((ok, fail) => canvas.toBlob((b) => (b ? ok(b) : fail(new Error('No se pudo crear la imagen'))), 'image/png'));
  return new File([blob], exportName(s, 'png'), { type: 'image/png' });
}

/**
 * Saves a file on this device: computers download it; phones open the share sheet (Guardar en
 * Archivos, Fotos, WhatsApp…) because iOS does not let a page download by itself.
 */
export async function saveFile(file: File): Promise<void> {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (detectPlatform() !== 'desktop' && nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: file.name });
      return;
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
    }
  }
  downloadFile(file);
}
